// Canonical enumerations shared by database, API, engine and UI.
// Values are stable identifiers; display strings live in the web locale files.

export const ROLES = ["admin", "maintainer", "operator", "reviewer", "observer"] as const;
export type Role = (typeof ROLES)[number];

/** Main Kanban columns (PRD §7), in board order. */
export const TASK_COLUMNS = [
  "backlog",
  "in_analysis",
  "todo",
  "in_progress",
  "testing",
  "in_review",
  "integrating",
  "awaiting_release",
  "done",
] as const;
export type TaskColumn = (typeof TASK_COLUMNS)[number];

/**
 * Side states (PRD §7). A task in a side state keeps its `return_column`. `awaiting_human` (D23): a decision only a
 * human may take (the council escalated it); shown in its own Kanban column and timed until the task resumes.
 */
export const TASK_SIDE_STATES = ["blocked", "waiting_resource", "paused", "failed", "cancelled", "awaiting_human"] as const;
export type TaskSideState = (typeof TASK_SIDE_STATES)[number];

export type TaskStatus = TaskColumn | TaskSideState;
export const TASK_STATUSES: readonly TaskStatus[] = [...TASK_COLUMNS, ...TASK_SIDE_STATES];

export const TERMINAL_TASK_STATUSES = ["done", "cancelled"] as const satisfies readonly TaskStatus[];

/**
 * Columns where an agent is actively working on the task: the board shows the current
 * attempt (agent profile, effective model) and a live "working for" timer for these.
 */
export const WORKING_TASK_STATUSES = ["in_analysis", "in_progress", "testing", "in_review", "integrating"] as const satisfies readonly TaskStatus[];
export type WorkingTaskStatus = (typeof WORKING_TASK_STATUSES)[number];

export const TASK_TYPES = [
  "feature",
  "bugfix",
  "investigation",
  "refactor",
  "test",
  "documentation",
  "operation",
] as const;
export type TaskType = (typeof TASK_TYPES)[number];

export const RISK_LEVELS = ["low", "medium", "high", "critical"] as const;
export type RiskLevel = (typeof RISK_LEVELS)[number];

export const TASK_ORIGINS = ["manual", "prd", "chat", "api", "github", "sentry", "system"] as const;
export type TaskOrigin = (typeof TASK_ORIGINS)[number];

/** Project default delivery target (RF-FLOW-04). */
export const DELIVERY_TARGETS = ["local_commit", "pull_request", "merge_confirmed", "deployment_validated"] as const;
export type DeliveryTarget = (typeof DELIVERY_TARGETS)[number];

export const PROJECT_STATES = ["draft", "active", "paused", "draining", "stopped"] as const;
export type ProjectState = (typeof PROJECT_STATES)[number];

export const AUTONOMY_PRESETS = [
  "observation",
  "autonomous_development",
  "autonomous_staging",
  "restricted_production",
  "autonomous_admin_isolated_dev",
] as const;
export type AutonomyPreset = (typeof AUTONOMY_PRESETS)[number];

export const ENVIRONMENT_KINDS = ["development", "preview", "staging", "production"] as const;
export type EnvironmentKind = (typeof ENVIRONMENT_KINDS)[number];

export const REQUIREMENT_KINDS = ["functional", "non_functional", "acceptance", "constraint"] as const;
export type RequirementKind = (typeof REQUIREMENT_KINDS)[number];

export const REQUIREMENT_STATES = ["pending", "planned", "covered", "ambiguous", "not_implementable", "superseded"] as const;
export type RequirementState = (typeof REQUIREMENT_STATES)[number];

export const EXTRACTION_STATES = ["complete", "partial", "failed", "unsupported"] as const;
export type ExtractionState = (typeof EXTRACTION_STATES)[number];

/** Precision of every measured duration, token count or cost (RF-OBS-03). */
export const PRECISIONS = ["measured", "estimated", "incomplete", "unknown"] as const;
export type Precision = (typeof PRECISIONS)[number];

/** Agent roles (RF-AGENT-01). Embedding/rerank are services, not roles. */
export const AGENT_ROLES = [
  "maestro",
  "assistant",
  "planner",
  "classifier",
  "researcher",
  "builder",
  "tester",
  "critic",
  "integrator",
  "compactor",
  /** Agent trainer (D20): reads scorecards and drafts new agent versions; never promotes them. */
  "coach",
] as const;
export type AgentRole = (typeof AGENT_ROLES)[number];

/** In-attempt agent loop implementations (D20). `legacy` is the original builder loop. */
export const AGENT_RUNTIMES = ["legacy", "langgraph"] as const;
export type AgentRuntime = (typeof AGENT_RUNTIMES)[number];

/** How a task's builder agent is chosen: the role's default profile, or the specialist matching the task. */
export const AGENT_SELECTION_MODES = ["profile", "specialist"] as const;
export type AgentSelectionMode = (typeof AGENT_SELECTION_MODES)[number];

/** Learning scopes, most specific first (retrieval precedence). */
export const MEMORY_SCOPES = ["agent_project", "project", "agent", "organization", "platform"] as const;
export type MemoryScope = (typeof MEMORY_SCOPES)[number];

export const LEARNING_KINDS = ["structure_issue", "practice", "pitfall", "procedure", "preference", "fact"] as const;
export type LearningKind = (typeof LEARNING_KINDS)[number];

export const LEARNING_STATUSES = ["proposed", "approved", "rejected", "deprecated", "superseded"] as const;
export type LearningStatus = (typeof LEARNING_STATUSES)[number];

export const VERDICTS = ["pass", "fail", "inconclusive"] as const;
export type Verdict = (typeof VERDICTS)[number];

export const SEVERITIES = ["info", "low", "medium", "high", "critical"] as const;
export type Severity = (typeof SEVERITIES)[number];

export const HEALTH_STATES = ["healthy", "degraded", "unavailable", "unknown"] as const;
export type HealthState = (typeof HEALTH_STATES)[number];

export const RUNTIME_STATES = ["running", "draining", "degraded", "stopped", "crash_loop"] as const;
export type RuntimeState = (typeof RUNTIME_STATES)[number];

/** Outcome of an external effect (RF-OPS-03): `unknown` requires reconciliation. */
export const EXTERNAL_OPERATION_STATES = ["pending", "succeeded", "failed", "unknown"] as const;
export type ExternalOperationState = (typeof EXTERNAL_OPERATION_STATES)[number];

export const INBOX_STATES = ["received", "processing", "processed", "failed", "dead_letter", "ignored"] as const;
export type InboxState = (typeof INBOX_STATES)[number];

export const CONFIG_STATES = ["draft", "validated", "active", "disabled"] as const;
export type ConfigState = (typeof CONFIG_STATES)[number];

/** Provenance/confidence of a quota or limit value (RF-EXEC-07, RF-RATE-06). */
export const VALUE_ORIGINS = ["configured", "reported", "estimated", "stale", "unknown"] as const;
export type ValueOrigin = (typeof VALUE_ORIGINS)[number];

/** Error classes for model/provider failures (RF-CFG-08). */
export const PROVIDER_ERROR_CLASSES = [
  "rate_limited",
  "credit_exhausted",
  "authentication",
  "model_not_found",
  "unavailable",
  "context_exceeded",
  "quality_rejected",
  "safety_refusal",
  "timeout_ambiguous",
  "invalid_request",
] as const;
export type ProviderErrorClass = (typeof PROVIDER_ERROR_CLASSES)[number];

export const CAPABILITY_STATES = ["declared", "verified", "failed", "unknown"] as const;
export type CapabilityState = (typeof CAPABILITY_STATES)[number];

/** Placement chosen when creating a task (additive; `backlog` is the historical behaviour). */
export const TASK_PLACEMENTS = ["backlog", "prioritized", "start_now"] as const;
/** F08 (AC-66): RAM/disk reservation class of a task; sizes live in the worker configuration. */
export const RESOURCE_CLASSES = ["light", "standard", "heavy"] as const;
export type ResourceClass = (typeof RESOURCE_CLASSES)[number];
export type TaskPlacement = (typeof TASK_PLACEMENTS)[number];

/** What the placement actually did. */
export const TASK_PLACEMENT_OUTCOMES = ["backlog", "prioritized", "started"] as const;
export type TaskPlacementOutcome = (typeof TASK_PLACEMENT_OUTCOMES)[number];

/** Why a placement fell back (or a caveat on a prioritized task). */
export const TASK_PLACEMENT_REASONS = [
  "dependencies_pending",
  "not_promotable",
  "priority_tied",
  "not_ready",
  "engine_unavailable",
  "project_not_active",
  "at_capacity",
  "engine_error",
] as const;
export type TaskPlacementReason = (typeof TASK_PLACEMENT_REASONS)[number];
